import React, { useEffect, useRef, useState, useCallback } from "react";
import { Square, X, MicOff } from "lucide-react";

const MAX_MS = 3 * 60 * 1000;        // hard cap: 3 minutes
const SILENCE_MS = 30 * 1000;        // silence safety net: 30s quiet -> countdown
const COUNTDOWN_FROM = 3;            // calm 3-2-1 before auto-stopping on silence
const RMS_THRESHOLD = 0.012;         // below this for SILENCE_MS counts as "quiet"

function pickMime() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (_) {}
  }
  return "";
}

function fmt(ms) {
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(1, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

// Earcon-free, silent voice capture. Records via MediaRecorder, monitors level with the Web
// Audio API for the silence safety-net, and hands a finished audio File to onComplete.
export default function VoiceRecorder({ open, onComplete, onCancel }) {
  const [elapsed, setElapsed] = useState(0);
  const [countdown, setCountdown] = useState(0);   // 0 = not counting down
  const [denied, setDenied] = useState(false);

  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("");
  const acRef = useRef(null);
  const rafRef = useRef(null);
  const startedRef = useRef(0);
  const lastSoundRef = useRef(0);
  const stoppedRef = useRef(false);
  const cancelledRef = useRef(false);

  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { acRef.current && acRef.current.close(); } catch (_) {}
    acRef.current = null;
    try {
      streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop());
    } catch (_) {}
    streamRef.current = null;
  }, []);

  const stop = useCallback((cancelled = false) => {
    if (stoppedRef.current) return;
    stoppedRef.current = true;
    cancelledRef.current = cancelled;
    try {
      if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
      else cleanup();
    } catch (_) { cleanup(); }
  }, [cleanup]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    stoppedRef.current = false;
    cancelledRef.current = false;
    setElapsed(0);
    setCountdown(0);
    setDenied(false);

    (async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (_) {
        if (active) setDenied(true);
        return;
      }
      if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;

      const mime = pickMime();
      mimeRef.current = mime;
      let rec;
      try {
        rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      } catch (_) {
        rec = new MediaRecorder(stream);
      }
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        cleanup();
        if (cancelledRef.current) return;
        const type = (mimeRef.current || "audio/webm").split(";")[0];
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        if (!blob.size) { onCancel && onCancel(); return; }
        const fileType = mimeRef.current || type;
        const file = new File([blob], `voice-capture.${ext}`, { type: fileType });
        onComplete && onComplete(file);
      };

      startedRef.current = Date.now();
      lastSoundRef.current = Date.now();
      try { rec.start(250); } catch (_) { rec.start(); }

      // Level monitoring (silence net + keeps recording truly silent — no Web Speech earcon).
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ac = new AC();
        acRef.current = ac;
        const src = ac.createMediaStreamSource(stream);
        const analyser = ac.createAnalyser();
        analyser.fftSize = 2048;
        src.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);

        const tick = () => {
          if (stoppedRef.current) return;
          const now = Date.now();
          const el = now - startedRef.current;
          setElapsed(el);
          if (el >= MAX_MS) { stop(false); return; }

          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);

          if (rms >= RMS_THRESHOLD) {
            lastSoundRef.current = now;
            setCountdown(0);
          } else {
            const quietFor = now - lastSoundRef.current;
            if (quietFor >= SILENCE_MS) {
              const remaining = COUNTDOWN_FROM - Math.floor((quietFor - SILENCE_MS) / 1000);
              if (remaining <= 0) { stop(false); return; }
              setCountdown(remaining);
            }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (_) {
        // No Web Audio (rare): fall back to a plain timer + max cap only.
        const id = setInterval(() => {
          if (stoppedRef.current) { clearInterval(id); return; }
          const el = Date.now() - startedRef.current;
          setElapsed(el);
          if (el >= MAX_MS) { clearInterval(id); stop(false); }
        }, 250);
      }
    })();

    return () => {
      active = false;
      stop(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 backdrop-blur-sm"
      data-testid="voice-recorder"
    >
      <div className="relative w-full sm:max-w-md mx-4 rounded-3xl bg-[var(--bg)] border border-[var(--line)] shadow-[0_-8px_40px_rgba(0,0,0,0.18)] px-6 py-9 sm:py-11 text-center">
        <button
          onClick={() => { stop(true); onCancel && onCancel(); }}
          data-testid="voice-cancel-btn"
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-[var(--surface2)] text-[var(--ink3)]"
          aria-label="Cancel"
        >
          <X size={18} />
        </button>

        {denied ? (
          <div className="flex flex-col items-center" data-testid="voice-denied">
            <span className="h-14 w-14 rounded-full bg-[var(--surface2)] flex items-center justify-center text-[var(--ink3)]">
              <MicOff size={24} strokeWidth={1.8} />
            </span>
            <p className="mt-5 text-[var(--ink)] text-base font-medium">Microphone is blocked</p>
            <p className="mt-1.5 text-sm text-[var(--ink3)] leading-relaxed max-w-xs">
              Allow microphone access in your browser settings, then try again — or type it instead.
            </p>
            <button
              onClick={() => { stop(true); onCancel && onCancel(); }}
              data-testid="voice-denied-close"
              className="mt-6 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] font-semibold px-6 py-2.5 text-sm transition-colors"
            >
              Type instead
            </button>
          </div>
        ) : (
          <>
            <div className="relative mx-auto h-24 w-24 flex items-center justify-center">
              <span className="held-halo held-halo--active" aria-hidden="true" />
              <span className="absolute inset-0 rounded-full bg-[var(--pine-soft)] animate-ping opacity-60" aria-hidden="true" />
              <span className="relative h-16 w-16 rounded-full bg-[var(--pine)] flex items-center justify-center">
                <span className="h-3 w-3 rounded-full bg-[var(--on-pine)] breathe" aria-hidden="true" />
              </span>
            </div>

            <p className="mt-6 text-[var(--pine)] text-lg font-medium" data-testid="voice-listening-label">
              Listening…
            </p>
            <p className="mt-1 text-2xl font-serif-display text-[var(--ink)] tabular-nums" data-testid="voice-timer">
              {fmt(elapsed)}
            </p>

            {countdown > 0 ? (
              <p className="mt-3 text-sm text-[var(--ink3)] leading-relaxed" data-testid="voice-countdown">
                Still there? Wrapping up in {countdown}…
              </p>
            ) : (
              <p className="mt-3 text-sm text-[var(--ink3)] leading-relaxed max-w-xs mx-auto" data-testid="voice-helper">
                Say everything. Tap Done when finished.
              </p>
            )}

            <button
              onClick={() => stop(false)}
              data-testid="voice-done-btn"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] font-semibold px-7 py-3 text-sm shadow-[0_8px_24px_rgba(44,90,77,0.25)] transition-colors"
            >
              <Square size={15} strokeWidth={2.25} fill="currentColor" />
              I'm done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
